import React from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Pagination, Segmented, Skeleton, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { RetrieveAssignmentSchema, RetrieveUserSchema } from '@app/models/users';

import { useLocalSelector } from '@app/services/hooks/useRedux';
import { Paper } from '@components/Paper';

import { useAssignmentsQuery, useRolesListQuery, useRuleAttributesQuery } from '../../hooks';

import { AssignmentItem } from './components/AssignmentItem';
import { AssignmentModal } from './components/AssignmentModal';

interface IProps {
  employee: RetrieveUserSchema;
  /** Просмотр карточки сотрудника: назначения видно, но выдать и отозвать нельзя. */
  readOnly?: boolean;
}

export const Assignments: React.FC<IProps> = ({ readOnly = false, employee }) => {
  // Отозванные назначения не удаляются и со временем копятся, поэтому по умолчанию
  // показываем только действующие, а остальные - по требованию.
  const [activeOnly, setActiveOnly] = React.useState(true);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<RetrieveAssignmentSchema | undefined>(undefined);
  const [pageSize, setPageSize] = React.useState(20);
  const [page, setPage] = React.useState(1);

  const currentUser = useLocalSelector((state) => state.currentSession.data);

  const assignments = useAssignmentsQuery(employee.pk, activeOnly, (page - 1) * pageSize, pageSize);
  const rolesQuery = useRolesListQuery();
  const ruleAttributesQuery = useRuleAttributesQuery();

  // Создание гейтится объектным правом scope, изменение/отзыв - $permissions конкретного назначения.
  const canManage =
    !readOnly &&
    !!currentUser &&
    currentUser.pk !== employee.pk &&
    currentUser.scope.$permissions.create_assignments === true;

  const assignmentsT = useTranslation(NSKeys.assignments);
  const commonT = useTranslation(NSKeys.common);

  const handleOpenCreate = () => {
    setEditing(undefined);
    setModalOpen(true);
  };

  const handleOpenEdit = (assignment: RetrieveAssignmentSchema) => {
    setEditing(assignment);
    setModalOpen(true);
  };

  const items = assignments.data?.items || [];
  const total = assignments.data?.total || 0;
  const roles = rolesQuery.data?.items || [];
  const roleName = (roleId: number) => roles.find((r) => r.pk === roleId)?.name || String(roleId);
  const ruleAttributes = ruleAttributesQuery.data?.orders || [];

  return (
    <Paper style={{ margin: 24, padding: '20px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
        <Space size={8} align="baseline">
          <Typography.Title level={4} style={{ margin: 0 }}>
            {assignmentsT.t('card.title')}
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {assignmentsT.t('card.subtitle')}
          </Typography.Text>
        </Space>

        <Space size={8} align="center">
          <Segmented
            size="small"
            value={activeOnly ? 'active' : 'all'}
            onChange={(value) => {
              setActiveOnly(value === 'active');
              setPage(1);
            }}
            options={[
              { label: assignmentsT.t('filter.active'), value: 'active' },
              { label: assignmentsT.t('filter.all'), value: 'all' },
            ]}
          />
          {canManage && (
            <Button icon={<PlusOutlined />} onClick={handleOpenCreate}>
              {assignmentsT.t('card.addButton')}
            </Button>
          )}
        </Space>
      </div>

      {assignments.isLoading ? (
        <Skeleton active />
      ) : assignments.isError ? (
        <Typography.Text type="danger">{commonT.t('dataNotFound')}</Typography.Text>
      ) : items.length === 0 ? (
        <Typography.Text type="secondary" italic>
          {activeOnly ? assignmentsT.t('card.emptyActive') : assignmentsT.t('card.empty')}
        </Typography.Text>
      ) : (
        <>
          {items.map((assignment) => (
            <AssignmentItem
              key={assignment.uid}
              assignment={assignment}
              roleName={roleName(assignment.role_id)}
              canManage={canManage}
              onEdit={handleOpenEdit}
            />
          ))}
          {total > pageSize && (
            <Pagination
              current={page}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              style={{ marginTop: 16, textAlign: 'center' }}
              onChange={(nextPage, nextPageSize) => {
                setPage(nextPageSize === pageSize ? nextPage : 1);
                setPageSize(nextPageSize);
              }}
            />
          )}
        </>
      )}

      {canManage && (
        <AssignmentModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          employee={employee}
          assignment={editing}
          roles={roles}
          rolesLoading={rolesQuery.isLoading}
          ruleAttributes={ruleAttributes}
        />
      )}
    </Paper>
  );
};
